/* eslint-disable max-len */
import React, { useContext, useEffect, useRef, useState } from 'react'
import AdvancedSearchInputBox from '../../../../components/AdvancedSearchInput/AdvancedSearchInputBox';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { resetWellnessReportSearch, setWellnessReportSearch } from '../../../../redux/wellnessReportSearchSlice';
import GlobalContext from '../../../../components/Context/GloablContext';

export default function WellnessReportSearch({ handleOnSubmit, emptyField, setEmptyField }) {
    const wellnessReportSearch = useSelector(state => state.WellnessReportSearchData);
    const { isSearchOpen, setIsSearchOpen, isSideBar, setIsSideBar } = useContext(GlobalContext);
    const [searchParams, setSearchParams] = useSearchParams()
    const dispatch = useDispatch()
    const btnRef = useRef()
    const sidebarPrvState = useSelector(state => state?.systemConfigData?.sidebarPrvState)
    const searchApplied = useSelector((state) => state.WellnessReportSearchData.setWellnessReportSearchApplied);

    // handleOnChangeUserDetails function
    const handleOnChangeUserDetails = (id, value) => {
        let updatedValue = value;
        if (id === 'duration_seconds' || id === 'duration_minutes' || id === 'duration_hours') {
            // Allow only numbers and dash (-)
            const regex = /^[0-9\-*]*$/;

            // If the value doesn't match the regex, set it to the current value (or empty string)
            if (!regex.test(value)) {
                updatedValue = updatedValue.replace(/[^0-9\-*]/g, ''); // Replace any non-numeric, non-dash character
            }
        }
        // Allow only numeric input for the "age" field
        if (id === 'age') {
            // Use a regular expression to allow only numbers
            updatedValue = updatedValue.replace(/\D/g, ''); // Removes any non-numeric characters
        }
        if (id === 'alert_title') {
            // Allow only numbers and the '*' character for 'alert_title'
            updatedValue = updatedValue.replace(/[^0-9*]/g, ''); // Removes any characters that are not digits or '*'
        }

        if (typeof updatedValue === 'string') {
            updatedValue = updatedValue;
        }

        const updatedState = { ...wellnessReportSearch, [id]: updatedValue };

        if (updatedValue === '' && updatedState[`${id}_inverse`] === 'true') {
            updatedState[`${id}_inverse`] = '';
        }
        const allEmpty =
            (!updatedState.duration_hours?.trim()) &&
            (!updatedState.duration_minutes?.trim()) &&
            (!updatedState.duration_seconds?.trim());
        if (allEmpty) {
            updatedState.duration_inverse = ''; // Uncheck checkbox
        }

        // Update the patientSearch state and dispatch to Redux
        setWellnessReportSearch(updatedState);
        dispatch(setWellnessReportSearch(updatedState));
    };

    //handleSelect checkbox functions
    const handleCheckboxChange = (e, field) => {
        const { checked } = e.target;

        // Create an updated patientSearch state
        let updatedState = { ...wellnessReportSearch };

        if (field === 'High' || field === 'Medium') {
            // Update the status array if it's a status-related checkbox
            const updatedalert_severity = checked
                ? [...(wellnessReportSearch.alert_severity || []), field]
                : (wellnessReportSearch.alert_severity || []).filter((alert_severity) => alert_severity !== field);

            // Uncheck alert_severity_inverse if the alert_severity array is empty
            if (updatedalert_severity.length === 0) {
                updatedState.alert_severity_inverse = '';
            }

            updatedState.alert_severity = updatedalert_severity;
        } else {
            // Handle other fields as boolean values
            updatedState[field] = checked ? 'true' : '';
        }

        // Dispatch the updated state
        dispatch(setWellnessReportSearch(updatedState));
    };


    const dateformatSysconfig = useSelector(
        (state) => state?.systemConfigData?.globalVariables?.date_format
    );

    const [dateFormat, setDateFormat] = useState('dd/mm/yyyy');

    // Update date format from sysconfig
    useEffect(() => {
        if (dateformatSysconfig) {
            const format = dateformatSysconfig.split(',')[0].toLowerCase();
            setDateFormat(format === 'mm/dd/yyyy' ? 'mm/dd/yyyy' : 'dd/mm/yyyy');
        }
    }, [dateformatSysconfig]);

    // Function to handle button click
    const handleCloseClick = () => {
        // Toggle the advanced search state
        setIsSearchOpen(!isSearchOpen)
        // If previous sidebar state was true and isSideBar is currently false, set it to true
        if (sidebarPrvState === true && !isSideBar) {
            setIsSideBar(true);
        }
        // Reset search state if searchApplied is false
        if (!searchApplied) {
            dispatch(resetWellnessReportSearch());
        }
    };

    /**
     * The function `isApplyButtonDisabled` checks if all values in the `wellnessReportSearch` object are
     * considered empty based on their type.
     * @returns The `isApplyButtonDisabled` function is returning a boolean value. It checks each
     * key-value pair in the `wellnessReportSearch` object and returns `true` if the value is considered
     * empty based on the conditions specified in the function. The function will return `true` if all
     * values in the `wellnessReportSearch` object (except for the 'match' key) are either empty arrays,
     */
    const isApplyButtonDisabled = () => {
        return Object.entries(wellnessReportSearch).every(([key, value]) => {
            if (key === 'match') return true; // Ignore 'match'

            // Check if value is an array
            if (Array.isArray(value)) {
                return value.length === 0; // Return true if the array is empty
            }

            // Check if value is a string
            if (typeof value === 'string') {
                return value.trim() === ''; // Trim and check if it's empty
            }

            // For any other type (objects, numbers, etc.), return true to indicate it's 'empty'
            return true;
        });
    };

    /*** The `handleClearAll` function resets parameters and clears search-related fields.*/
    const handleClearAll = () => {
        if (btnRef.current) {
            btnRef.current.disabled = true; // Disable the button
        }
        dispatch(resetWellnessReportSearch());
        const updatedParams = new URLSearchParams(searchParams);
        updatedParams.delete('search');
        updatedParams.delete('search_mode');
        setTimeout(() => {
            setSearchParams(Object.fromEntries(updatedParams.entries()));
            setEmptyField(false);

            if (btnRef.current) {
                btnRef.current.disabled = false; // Re-enable after operation
            }
        }, 300);
    };

    /* The above code is using the `useEffect` hook in a React component to add an event listener for
    the "keydown" event on the window object. When a key is pressed, it checks if the key pressed is
    the "Escape" key, and if so, it calls the `handleClearAll` function. The `useEffect` hook also
    includes a cleanup function that removes the event listener when the component unmounts or when
    the dependencies array is empty (in this case, it is empty, so it will only run once when the
    component mounts). */
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                handleClearAll();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    /* The above code snippet is a React useEffect hook that is triggered when the
    `isSearchOpen` dependency changes. Within the useEffect hook, it performs the following
    tasks: */
    useEffect(() => {
        dispatch(resetWellnessReportSearch())

        const params = Object.fromEntries(searchParams);

        if (params.search) {
            // Decode the search string to get individual fields
            const searchDecoded = decodeURIComponent(params.search);

            // Existing matches for patient_id, age, alert_title and other states
            const patIdMatch = searchDecoded.match(/(patient_id)=(ne:)?(.+?)(;|$)/);
            const userIdMatch = searchDecoded.match(/(user_id)=(ne:)?(.+?)(;|$)/);
            const durationMatch = searchDecoded.match(/(end_time)=(ne:)?(.+?)(;|$)/);
            const notesMatch = searchDecoded.match(/(notes)=(ne:)?(.+?)(;|$)/);

            // Build the new state
            const updatedState = {};

            // handle patient_id field
            if (patIdMatch) {
                const isInverse = !!patIdMatch[2]; // Check if `ne:` is present
                const fieldValue = patIdMatch[3]; // Extract the value

                // Update the shared state fields
                updatedState.patient_id = fieldValue === '__NULL__' ? '-' : fieldValue;
                updatedState.patient_inverse = isInverse ? 'true' : '';
            }
            // Handle userIdMatch field
            if (userIdMatch) {
                const isInverse = !!userIdMatch[2]; // Check if `ne:` is present
                const fieldValue = userIdMatch[3]; // Extract the value

                updatedState.user_id = fieldValue === '__NULL__' ? '-' : fieldValue;
                updatedState.user_id_inverse = isInverse ? 'true' : '';
            }
            // Handle userIdMatch field
            if (notesMatch) {
                const isInverse = !!notesMatch[2]; // Check if `ne:` is present
                const fieldValue = notesMatch[3]; // Extract the value

                updatedState.notes = fieldValue === '__NULL__' ? '-' : fieldValue;
                updatedState.notes_inverse = isInverse ? 'true' : '';
            }
            // Handle duration field if matched

            if (durationMatch) {
                const endTime = durationMatch[3];  // This will give the decoded end_time value like "*0 Hrs:0 Mins:10 Secs*"

                // Check if end_time is "__NULL__"
                if (endTime === '__NULL__') {
                    updatedState.duration_hours = '-';
                    updatedState.duration_minutes = '-';
                    updatedState.duration_seconds = '-';
                }
                // Check if end_time is "*"
                else if (endTime === '*') {
                    updatedState.duration_hours = '*';
                    updatedState.duration_minutes = '*';
                    updatedState.duration_seconds = '*';
                } else {
                    // Now, use a second regex to extract hours, minutes, and seconds
                    const timeMatch = endTime.match(/\*([0-9]+) Hrs:([0-9]+) Mins:([0-9]+) Secs\*/);

                    if (timeMatch) {
                        const durationHours = timeMatch[1];  // Hours
                        const durationMinutes = timeMatch[2];  // Minutes
                        const durationSeconds = timeMatch[3];  // Seconds

                        // Set the duration values in the updated state
                        updatedState.duration_hours = durationHours;
                        updatedState.duration_minutes = durationMinutes;
                        updatedState.duration_seconds = durationSeconds;
                    }
                }
            }
            // Handle search_mode if applicable
            if (params.search_mode) {
                updatedState.match = params.search_mode === 'or' ? true : false;
            }

            // Dispatch the action to update Redux state
            setTimeout(() => {
                dispatch(setWellnessReportSearch(updatedState));
            }, 0);
        }
    }, [isSearchOpen]);

    return (
        <div className='absolute top-0 left-0 w-[267px] rounded-tr-[12px] rounded-br-[12px] shadow-s7 h-[100dvh] bg-[#ffffff] z-[100]'>
            <form action=''
                onSubmit={(e) => {
                    handleOnSubmit(e);
                }} // Existing submit handler
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault(); 
                        handleOnSubmit(e);
                    }
                }}
                className='w-full px-3 py-4'>
                <section className='flex justify-between items-center'>
                    <p className='text-sm font-semibold text-[#52555D]'>Advanced Search</p>
                    <button type='button' onClick={handleCloseClick}>
                        <img src="/images/cross_mark_gray.svg" alt="close-icon" loading='lazy' decoding='async' />
                    </button>
                </section>
                <section className='mt-4 flex items-center pb-4 border-b border-[#C5C9DA]'>
                    <p className='text-xs font-normal text-[#000000] pr-2'>Match:</p>
                    <div className='flex items-center'>
                        <div className='flex items-center'>
                            <div className="relative custom-radioBlue flex items-center">
                                <input id="match_any" type="radio" name="default-radio1" checked={wellnessReportSearch?.match} onChange={() => handleOnChangeUserDetails("match", true)} />
                                <label htmlFor="match_any" className={`text-xs font-normal ${wellnessReportSearch?.match ? 'text-[#000000]' : 'text-[#52555D]'} pl-6 pr-2 w-full cursor-pointer`}>
                                    Any
                                </label>
                            </div>
                            <div className='relative'>
                                <span className='any_hover'>
                                    <img src="/images/Tooltip.svg" alt="icon" loading='lazy' decoding='async' className='w-[15px] h-[15px] mt-[1px]' />
                                </span>
                                <p className='bg-[#000000] bg-opacity-75 p-2 text-xs font-normal text-[#FFFFFF] transition-all absolute top-[25px] left-[0px] any_hover_text hidden rounded-lg w-[212px] z-[1]'>
                                    Displays results that match any of the input values. <br />
                                    Example: A OR B will return results that match either A, B, or both.
                                </p>
                            </div>
                        </div>
                        <div className='flex items-center ml-4'>
                            <div className="relative custom-radioBlue flex items-center ml-3" >
                                <input id="match_all" type="radio" name="default-radio1" checked={!wellnessReportSearch?.match} onChange={() => handleOnChangeUserDetails("match", false)} />
                                <label htmlFor="match_all" className={`text-xs font-normal ${!wellnessReportSearch?.match ? 'text-[#000000]' : 'text-[#52555D]'} pl-6 pr-2 w-full cursor-pointer`}>
                                    All
                                </label>
                            </div>
                            <div className='relative'>
                                <span className='any_hover'>
                                    <img src="/images/Tooltip.svg" alt="icon" loading='lazy' decoding='async' className='w-[15px] h-[15px] mt-[1px]' />
                                </span>
                                <p className='bg-[#000000] bg-opacity-75 p-2 text-xs font-normal text-[#FFFFFF] transition-all absolute top-[25px] left-[0px] any_hover_text hidden rounded-lg w-[212px] z-[1]'>
                                    Displays results that match all of the input values. <br />
                                    Example: A AND B will only return results that match both A and B.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
                <div className='w-full flex items-center my-3'>
                    <div className='w-[170px] mr-2'>
                        <p className='text-xs font-normal text-[#52555D]'>Column Name & Value</p>
                    </div>
                    <div className='relative w-[65px] flex items-center'>
                        <p className='text-xs font-normal text-[#52555D]'>Inverse</p>
                        <span className='any_hover z-[0] ml-1'>
                            <img src="/images/Tooltip.svg" alt="icon" loading='lazy' decoding='async' className='w-[15px] h-[15px]' />
                        </span>
                        <p className='bg-[#000000] bg-opacity-75 p-2 text-xs font-normal text-[#FFFFFF] transition-all absolute top-[25px] left-[0px] any_hover_text hidden rounded-lg w-[212px] z-[10]'>
                            Excludes results that match the input value. <br />
                            Example: NOT A will return all results except those that match A.
                        </p>
                    </div>
                </div>
                <div className='-ml-2'>
                    <div className={`overflow-auto ${emptyField ? 'h-[calc(100dvh-302px)]' : 'h-[calc(100dvh-285px)]'} scrollbar_purple pl-2`}>
                        <div className='w-full flex border-b border-[#C5C9DA66] pb-2'>
                            <div className='w-[170px] mr-2'>
                                <AdvancedSearchInputBox
                                    label={`Patient ID`}
                                    placeholder={`Enter patient ID`}
                                    id={"patient_id"}
                                    data_testid={"patient_id"}
                                    htmlFor={"patient_id"}
                                    value={wellnessReportSearch.patient_id}
                                    handleOnChangeInput={handleOnChangeUserDetails}
                                    maxLength={60}
                                />
                            </div>
                            <div className='relative w-[65px] mt-[20px]'>
                                <div className='p-[8px] flex items-center mark-check mark-check1 mark-check2 relative checkbox custom-checkBoxBlue1'>
                                    <input
                                        type='checkbox'
                                        id='patient_id_inverse'
                                        onChange={(e) => handleCheckboxChange(e, 'patient_id_inverse')}
                                        checked={wellnessReportSearch.patient_id_inverse === 'true'}
                                        className='h-[18px] w-[18px] cursor-pointer z-[11] disabled:cursor-not-allowed'
                                        disabled={wellnessReportSearch?.patient_id?.trim() === ""}
                                    />
                                    <label htmlFor='patient_id_inverse' className='text-[16px] font-normal text-[#000] ml-[16px] cursor-pointer'>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className='w-full flex border-b border-[#C5C9DA66] pb-2 pt-1'>
                            <div className='w-[170px] mr-2'>
                                <AdvancedSearchInputBox
                                    label={"User ID"}
                                    placeholder={"Enter user ID"}
                                    id={"user_id"}
                                    data_testid={"user_id"}
                                    htmlFor={"user_id"}
                                    value={wellnessReportSearch?.user_id}
                                    handleOnChangeInput={handleOnChangeUserDetails}
                                    maxLength={60}
                                />
                            </div>
                            <div className='relative w-[65px] mt-[20px]'>
                                <div className='p-[8px] flex items-center mark-check mark-check1 mark-check2 relative checkbox custom-checkBoxBlue1'>
                                    <input
                                        type='checkbox'
                                        id='user_id_inverse'
                                        onChange={(e) => handleCheckboxChange(e, 'user_id_inverse')}
                                        checked={wellnessReportSearch?.user_id_inverse === 'true'}
                                        className='h-[18px] w-[18px] cursor-pointer z-[11] disabled:cursor-not-allowed'
                                        disabled={wellnessReportSearch?.user_id?.trim() === ""}
                                    />
                                    <label htmlFor='user_id_inverse' className='text-[16px] font-normal text-[#000] ml-[16px] cursor-pointer'>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className='w-full flex border-b border-[#C5C9DA66] pb-2 pt-1'>
                            <div className='mr-2'>
                                <p className='font-normal text-xs text-black py-1'>Duration</p>
                                <div className="flex items-center gap-2 w-[170px]">
                                    <input
                                        type="text"
                                        name="hours"
                                        id='duration_hours'
                                        value={wellnessReportSearch.duration_hours}
                                        onChange={(e) => handleOnChangeUserDetails('duration_hours', e.target.value)}
                                        placeholder="Hrs"
                                        className="w-1/3 h-7 border border-[#C5C9DA] text-center text-black font-normal text-xs rounded-lg px-2 py-[6px] placeholder:text-[#52555D]"
                                        maxLength={2}
                                        autoComplete='off'
                                    />
                                    <span>:</span>
                                    <input
                                        type="text"
                                        name="minutes"
                                        id='duration_minutes'
                                        value={wellnessReportSearch.duration_minutes}
                                        onChange={(e) => handleOnChangeUserDetails('duration_minutes', e.target.value)}
                                        placeholder="Mins"
                                        className="w-1/3 h-7 border border-[#C5C9DA] text-center text-black font-normal text-xs rounded-lg px-2 py-[6px] placeholder:text-[#52555D]"
                                        maxLength={2}
                                        autoComplete='off'
                                    />
                                    <span>:</span>
                                    <input
                                        type="text"
                                        name="seconds"
                                        id='duration_seconds'
                                        value={wellnessReportSearch.duration_seconds}
                                        onChange={(e) => handleOnChangeUserDetails('duration_seconds', e.target.value)}
                                        placeholder="Secs"
                                        className="w-1/3 h-7 border border-[#C5C9DA] text-center text-black font-normal text-xs rounded-lg px-2 py-[6px] placeholder:text-[#52555D]"
                                        maxLength={2}
                                        autoComplete='off'
                                    />
                                </div>
                            </div>
                            <div className='relative w-[65px] mt-[20px]'>
                                <div className='p-[8px] flex items-center mark-check mark-check1 mark-check2 relative checkbox custom-checkBoxBlue1'>
                                    <input
                                        type='checkbox'
                                        id='duration_inverse'
                                        onChange={(e) => handleCheckboxChange(e, 'duration_inverse')}
                                        checked={wellnessReportSearch?.duration_inverse === 'true'}
                                        className='h-[18px] w-[18px] cursor-pointer z-[11] disabled:cursor-not-allowed'
                                        disabled={
                                            !(
                                                wellnessReportSearch?.duration_hours?.trim() ||
                                                wellnessReportSearch?.duration_minutes?.trim() ||
                                                wellnessReportSearch?.duration_seconds?.trim()
                                            )
                                        }
                                    />
                                    <label htmlFor='duration_inverse' className='text-[16px] font-normal text-[#000] ml-[16px] cursor-pointer'>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className='w-full flex border-b border-[#C5C9DA66] pb-2 pt-1'>
                            <div className='w-[170px] mr-2'>
                                <AdvancedSearchInputBox
                                    label={"Notes"}
                                    placeholder={"Enter notes"}
                                    id={"notes"}
                                    data_testid={"notes"}
                                    htmlFor={"notes"}
                                    value={wellnessReportSearch?.notes}
                                    handleOnChangeInput={handleOnChangeUserDetails}
                                    maxLength={300}
                                />
                            </div>
                            <div className='relative w-[65px] mt-[20px]'>
                                <div className='p-[8px] flex items-center mark-check mark-check1 mark-check2 relative checkbox custom-checkBoxBlue1'>
                                    <input
                                        type='checkbox'
                                        id='notes_inverse'
                                        onChange={(e) => handleCheckboxChange(e, 'notes_inverse')}
                                        checked={wellnessReportSearch?.notes_inverse === 'true'}
                                        className='h-[18px] w-[18px] cursor-pointer z-[11] disabled:cursor-not-allowed'
                                        disabled={wellnessReportSearch?.notes?.trim() === ""}
                                    />
                                    <label htmlFor='notes_inverse' className='text-[16px] font-normal text-[#000] ml-[16px] cursor-pointer'>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='border-t border-[#C5C9DA]'>
                    <section className='mt-2'>
                        <p className='text-xs font-normal text-[#52555D]'>Note:</p>
                        <ol className='list-decimal pl-4 text-xs font-normal text-[#52555D]'>
                            <li>Use a dash (-) to indicate a null value.</li>
                            <li>Use an asterisk (*) as a wild-card character.</li>
                        </ol>
                    </section>
                </div>
                <div className="flex mt-[13px] justify-between">
                    <button
                        id="Cancel"
                        type='button'
                        ref={btnRef}
                        onClick={() => handleClearAll()}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault(); // Prevent form submission
                                handleClearAll(); // Perform the clear action
                            }
                        }}

                        className="py-2.5 px-4 rounded-[16px] border border-[#972E4C] text-sm font-semibold text-[#972E4C] min-w-fit focus:bg-[#edc9d3]"
                    >Clear All</button>
                    <button
                        type='submit'
                        id="apply-filter"
                        disabled={isApplyButtonDisabled()}
                        className={`py-2.5 w-[106px] px-4 rounded-[16px]  disabled:bg-[#777C88] bg-[#972E4C] hover:bg-[#AB556E] border border-[#972E4C]'} text-sm font-semibold text-[#FFFFFF] hover:duration-75 reset_password_conf`}>
                        Apply
                    </button>
                </div>
            </form>
        </div>
    )
}
